import SaveStatus from '@/components/ui/custom/dataDisplay/SaveStatus';
import Tabs from '@/components/ui/custom/dataDisplay/Tabs/Tabs';
import ImageUpload from '@/components/ui/custom/input/ImageUpload';
import { LessonDTO } from '@/data/services/lessonServices';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import { adapterLessonToLessonFields } from '@/utils/lesson/adapter';
import { formatLessonNamePrefix } from '@/utils/lesson/name';
import { ReactNode, useEffect } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import TitleForm from '../TitleForm';
import { useLessonTabs } from './LessonBox.hooks';
import LessonActions from './actions/LessonActions';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
export type LessonFields = Omit<LessonDTO, 'tools'> & {
  tool: number;
};

type ClassNames = Partial<{
  tabsContainer: string;
  tabsContent: string;
}>;

type LessonBoxFormProps = {
  lesson: Lesson;
  block?: TempLessonBlock;
  course?: CoursePath;
  isLoading?: boolean;
  hasChanges?: boolean;
  onSubmit: (params: { lesson: Partial<Lesson>; validated: boolean }) => void;
  updateLessons?: (lessonId?: number) => Promise<void>;
  className?: ClassNames;
  actions?: ReactNode;
  backUrl?: string;
};

const LessonBoxForm = ({
  lesson,
  block,
  course,
  hasChanges,
  onSubmit,
  isLoading,
  updateLessons,
  className,
  actions,
  backUrl,
}: LessonBoxFormProps) => {
  const { activeTab, setActiveTab, tabs, panes } = useLessonTabs(
    lesson,
    course,
    block,
    updateLessons,
  );

  const methods = useForm<LessonFields>({
    defaultValues: adapterLessonToLessonFields(lesson),
  });

  const { control, watch, trigger } = methods;

  const imageField = useController({
    control,
    name: 'bannerImg',
  });

  useEffect(() => {
    const subscription = watch(async lesson => {
      const isValid = await trigger();
      onSubmit({ lesson: lesson as Lesson, validated: isValid });
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onSubmit, trigger]);

  return (
    <FormProvider {...methods}>
      <header className="relative flex gap-2.5 items-center justify-between">
        <div className="flex gap-2 items-center">
          <ConditionalRenderer condition={!!backUrl}>
            <IconButton
              className="text-primary"
              icon={<ChevronLeftIcon className="w-8" />}
              href={backUrl}
            />
          </ConditionalRenderer>
          <ImageUpload
            {...imageField.field}
            accept=".png,.jpg,.jpeg"
            className={{
              div: twMerge(
                'min-h-10 h-10 aspect-video rounded-md',
                !course && 'h-14',
              ),
              base: 'w-fit',
              img: 'rounded-md max-w-full max-h-full',
              icon: 'w-6',
              figure: 'w-3',
              button: 'p-0.5 top-1 right-1',
            }}
          />
          <div className="flex gap-2 items-center">
            <ConditionalRenderer condition={lesson.order}>
              <h3 className={twMerge('text-primary !text-20 font-500')}>
                {formatLessonNamePrefix({
                  lessonOrder: lesson.order,
                }) + ' - '}
              </h3>
            </ConditionalRenderer>

            <TitleForm
              control={control}
              fieldName="name"
              className={{
                span: twMerge(
                  'text-primary !text-20 font-500 lg:max-w-[350px] xl:max-w-[600px] truncate',
                  !course && '!text-28',
                ),
                icon: 'text-accent',
              }}
            />
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <SaveStatus isLoading={isLoading} hasChanges={hasChanges} />
          <LessonActions lesson={lesson} coursePath={course} block={block} />
          {actions}
        </div>
      </header>

      <Tabs
        active={activeTab}
        contents={tabs}
        onChange={setActiveTab}
        color="bg-secondary-content"
      >
        <Tabs.Container className={className?.tabsContainer}>
          <Tabs.List>
            {panes.map(({ key, text, icon: Icon }) => (
              <Tabs.Trigger
                key={key}
                id={key}
                className={twMerge(
                  'gap-2',
                  activeTab === key ? 'text-secondary' : 'text-primary',
                )}
              >
                <Icon className="w-6" />
                {text}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Tabs.ShowContent
            className={{
              section: twMerge(
                'bg-secondary-content p-3.5',
                className?.tabsContent,
              ),
              aside: 'bg-base-100',
            }}
          />
        </Tabs.Container>
      </Tabs>
    </FormProvider>
  );
};

export default LessonBoxForm;
